import { Autocomplete, Grid, TextField, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PlanningAppealsFilter } from "@shared/publicportal/contracts/PlanningAppealsFilter";
import { Dayjs } from "dayjs";
import { SearchHelper } from "../../pages/search/SearchHelper";
import { PlanningAppealsSearchState } from "../../pages/search/PlanningAppeal/PlanningAppealsSearchState";
import PlanningAppealsSimpleSearch from "../../pages/search/PlanningAppeal/PlanningAppealsSimpleSearch";
import "../../pages/search/Search.css";
import datePickerValueFormatter from "@mirage/components/helpers/datePickerValueFormatter";

interface PlanningAppealsAdvSearchProps {
    state: PlanningAppealsSearchState;
    setter: (modifier: Partial<PlanningAppealsFilter>) => void;
}

const PlanningAppealsAdvancedSearch: React.FC<PlanningAppealsAdvSearchProps> = (props) => {
    const { state, setter } = props;

    return (
        <>
            <PlanningAppealsSimpleSearch setter={props.setter} state={props.state} />
            <Grid container className="search-container" sx={{ width: { sm: "100%", md: "80%", lg: "60%", xl: "70%" } }}>
                <Grid size={{ xs: 12, md: 12, xl: 12 }}>
                    <Typography variant="h5">Details</Typography>
                </Grid>
                {getDetailFields().map((field) => {
                    return (
                        <Grid size={{ xs: 12, md: 6, xl: 4 }} key={"p-appeal-details-" + field.key}>
                            {field}
                        </Grid>
                    );
                })}
            </Grid>
            <Grid container className="search-container" sx={{ width: { sm: "100%", md: "80%", lg: "60%", xl: "45%" } }}>
                <Grid size={{ xs: 12, md: 12, xl: 12 }}>
                    <Typography variant="h5">Date Fields</Typography>
                </Grid>
                {getDateFields().map((field) => {
                    return (
                        <Grid size={{ xs: 12, md: 6, xl: 6 }} key={"p-appeal-dates-" + field.key}>
                            {field}
                        </Grid>
                    );
                })}
            </Grid>
        </>
    );

    function getDetailFields() {
        return [
            <Autocomplete
                options={state.appealTypes || []}
                id="appeal-Type"
                className="search-form-autocomplete-field autocomplete-width-18rem"
                key="appeal-Type-key-form-control"
                size="small"
                value={props.state.filter.appealType?.length ? props.state.filter.appealType : null}
                inputValue={state.filter.appealType}
                onInputChange={(event, value) => event && setter({ appealType: value })}
                renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Appeal Type")}
            />,
            <Autocomplete
                options={state.wards || []}
                inputValue={state.filter.ward}
                value={props.state.filter.ward?.length ? props.state.filter.ward : null}
                onInputChange={(event, value) => event && setter({ ward: value })}
                key="appeal-Ward-key"
                size="small"
                id="appeal-Ward"
                className="search-form-autocomplete-field autocomplete-width-18rem"
                renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Appeal Ward")}
            />,
            <Autocomplete
                options={state.parishes || []}
                inputValue={state.filter.parish}
                value={props.state.filter.parish?.length ? props.state.filter.parish : null}
                onInputChange={(event, value) => event && setter({ parish: value })}
                key="appeal-Parish-key"
                size="small"
                id="appeal-Parish"
                className="search-form-autocomplete-field autocomplete-width-18rem"
                renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Appeal Parish")}
            />,
            <Autocomplete
                options={state.appealDecisionTypes || []}
                id="appeal-Decision-Type-label"
                className="search-form-autocomplete-field autocomplete-width-18rem"
                key="appeal-Decision-Type-key"
                size="small"
                value={props.state.filter.decisionType?.length ? props.state.filter.decisionType : null}
                inputValue={state.filter.decisionType}
                onInputChange={(event, value) => event && setter({ decisionType: value })}
                renderInput={(params) => SearchHelper.renderAutocompleteInput(params, "Decision Type")}
            />,
            <TextField
                value={state.filter.appealAddress}
                onChange={(e) => setter({ appealAddress: e.target.value })}
                key="appeal-Address-key"
                size="small"
                id="appeal-Address"
                label="Appeal Address"
                variant="outlined"
                sx={{ width: "100%" }}
            />,
        ];
    }

    function getDateFields() {
        return [
            <LocalizationProvider dateAdapter={AdapterDayjs} key="appeal-Lodged-Date-From-key">
                <DatePicker
                    value={datePickerValueFormatter(state.filter.appealLodgedDateFrom)}
                    onChange={(value: Dayjs | null) => setter({ appealLodgedDateFrom: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("appeal-Lodged-Date-From") } }}
                    label="Appeal Lodged Date From"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="appeal-Lodged-Date-To-key">
                <DatePicker
                    value={datePickerValueFormatter(state.filter.appealLodgedDateTo)}
                    onChange={(value: Dayjs | null) => setter({ appealLodgedDateTo: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("appeal-Lodged-Date-To") } }}
                    label="Appeal Lodged Date To"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="appeal-Created-Date-From-key">
                <DatePicker
                    value={datePickerValueFormatter(state.filter.appealCreatedDateFrom)}
                    onChange={(value: Dayjs | null) => setter({ appealCreatedDateFrom: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("appeal-Created-Date-From") } }}
                    label="Appeal Created Date From"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="appeal-Created-Date-To-key">
                <DatePicker
                    value={datePickerValueFormatter(state.filter.appealCreatedDateTo)}
                    onChange={(value: Dayjs | null) => setter({ appealCreatedDateTo: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("appeal-Created-Date-To") } }}
                    label="Appeal Created Date To"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="appeal-Decision-Date-From-key">
                <DatePicker
                    value={datePickerValueFormatter(state.filter.appealDecisionDateFrom)}
                    onChange={(value: Dayjs | null) => setter({ appealDecisionDateFrom: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("appeal-Decision-Date-From") } }}
                    label="Appeal Decision Date From"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
            <LocalizationProvider dateAdapter={AdapterDayjs} key="appeal-Decision-Date-To-key">
                <DatePicker
                    value={datePickerValueFormatter(state.filter.appealDecisionDateTo)}
                    onChange={(value: Dayjs | null) => setter({ appealDecisionDateTo: value?.toDate() })}
                    slotProps={{ textField: { ...SearchHelper.dateInputProps("appeal-Decision-Date-To") } }}
                    label="Appeal Decision Date To"
                    className="min-width-18rem"
                />
            </LocalizationProvider>,
        ];
    }
};

export default PlanningAppealsAdvancedSearch;
